import type { Vec2 } from '../core/types';

export interface LoopVertex {
  x: number;
  y: number;
  /** kanyar sugara; a rövid élekhez automatikusan csökken */
  r: number;
}

const dist = (a: Vec2, b: Vec2): number => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Lekerekített, zárt hurok tetszőleges (konvex vagy homorú) sokszögből.
 * A Track Catmull-Rom spline-ja simítja tovább, de így a kanyarok sugara kézben tartható.
 */
export function roundedLoop(vertices: LoopVertex[], step = 70): Vec2[] {
  const n = vertices.length;
  const pts: Vec2[] = [];

  interface Corner {
    a: Vec2;
    b: Vec2;
    cx: number;
    cy: number;
    r: number;
    a0: number;
    a1: number;
    sign: number;
  }

  const corners: Corner[] = [];
  for (let i = 0; i < n; i++) {
    const prev = vertices[(i + n - 1) % n];
    const cur = vertices[i];
    const next = vertices[(i + 1) % n];
    const inLen = dist(prev, cur);
    const outLen = dist(cur, next);
    if (inLen < 1 || outLen < 1) continue;

    const dix = (cur.x - prev.x) / inLen;
    const diy = (cur.y - prev.y) / inLen;
    const dox = (next.x - cur.x) / outLen;
    const doy = (next.y - cur.y) / outLen;
    const cross = dix * doy - diy * dox;
    const dot = Math.max(-1, Math.min(1, dix * dox + diy * doy));
    const turn = Math.acos(dot);

    if (turn < 0.02) {
      // gyakorlatilag egyenes: nincs ív
      corners.push({ a: { x: cur.x, y: cur.y }, b: { x: cur.x, y: cur.y }, cx: cur.x, cy: cur.y, r: 0, a0: 0, a1: 0, sign: 0 });
      continue;
    }

    const tanHalf = Math.tan(turn / 2);
    let tangent = cur.r * tanHalf;
    tangent = Math.min(tangent, inLen * 0.44, outLen * 0.44);
    const r = Math.max(20, tangent / tanHalf);

    const a = { x: cur.x - dix * tangent, y: cur.y - diy * tangent };
    const b = { x: cur.x + dox * tangent, y: cur.y + doy * tangent };
    const sign = cross >= 0 ? 1 : -1;
    const nx = -diy;
    const ny = dix;
    const cx = a.x + nx * r * sign;
    const cy = a.y + ny * r * sign;

    let a0 = Math.atan2(a.y - cy, a.x - cx);
    let a1 = Math.atan2(b.y - cy, b.x - cx);
    if (sign > 0) {
      while (a1 <= a0) a1 += Math.PI * 2;
    } else {
      while (a1 >= a0) a1 -= Math.PI * 2;
    }
    corners.push({ a, b, cx, cy, r, a0, a1, sign });
  }

  const line = (from: Vec2, to: Vec2): void => {
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    if (len < 0.5) return;
    const steps = Math.max(1, Math.round(len / step));
    for (let k = 0; k < steps; k++) {
      const t = k / steps;
      pts.push({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t });
    }
  };

  for (let i = 0; i < corners.length; i++) {
    const prev = corners[(i + corners.length - 1) % corners.length];
    const cur = corners[i];
    line(prev.b, cur.a);
    if (cur.r > 0) {
      const arcLen = Math.abs(cur.a1 - cur.a0) * cur.r;
      const steps = Math.max(3, Math.round(arcLen / 38));
      for (let k = 1; k <= steps; k++) {
        const ang = cur.a0 + ((cur.a1 - cur.a0) * k) / steps;
        pts.push({ x: cur.cx + Math.cos(ang) * cur.r, y: cur.cy + Math.sin(ang) * cur.r });
      }
    }
  }

  // az ívek és egyenesek találkozásánál keletkezhetnek egybeeső pontok: ezek cusp-ot okoznak
  const out: Vec2[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.5) out.push(p);
  }
  while (out.length > 2 && Math.hypot(out[0].x - out[out.length - 1].x, out[0].y - out[out.length - 1].y) <= 0.5) {
    out.pop();
  }
  // egyenletes pontköz: a Catmull-Rom így nem lő túl az ív/egyenes találkozásnál
  return resampleLoop(out, 46);
}

/** Zárt vonallánc újramintavételezése egyenletes ívhossz-közön. */
export function resampleLoop(points: Vec2[], spacing: number): Vec2[] {
  if (points.length < 3) return points;
  const total = points.reduce((sum, p, i) => {
    const q = points[(i + 1) % points.length];
    return sum + Math.hypot(q.x - p.x, q.y - p.y);
  }, 0);
  const count = Math.max(8, Math.round(total / spacing));
  const step = total / count;
  const out: Vec2[] = [];
  let seg = 0;
  let segStart = 0;
  let segLen = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
  for (let i = 0; i < count; i++) {
    const target = i * step;
    while (segStart + segLen < target && seg < points.length - 1) {
      segStart += segLen;
      seg++;
      const a = points[seg];
      const b = points[(seg + 1) % points.length];
      segLen = Math.hypot(b.x - a.x, b.y - a.y);
    }
    const a = points[seg];
    const b = points[(seg + 1) % points.length];
    const t = segLen > 0 ? (target - segStart) / segLen : 0;
    out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return out;
}
